import type {OmitStrict} from 'type-zoo'
import type {TMajorActionDiscardData} from '@kapitalist/contract/types'
import {compact} from '@kapitalist/utils/falsiness'
import {cardFactory} from '~/models/game/factories'
import {PlayerGamePosition} from '~/models/game/GamePosition'
import type {TMajorActionUserFacingDescriptionData} from '~/models/game/MajorAction'
import MajorAction from '~/models/game/MajorAction'
import type {AnyCard} from '~/models/game/types'
import {mutateIf} from '~/utils/etc'
import {inTense, pluralize, sentenceCase} from '~/utils/language'

type PublicMembersFromApi = OmitStrict<TMajorActionDiscardData, 'cards' | 'type'>

export default class MajorActionDiscard
  extends MajorAction<TMajorActionDiscardData>
  implements PublicMembersFromApi
{
  cards: AnyCard[]

  constructor(data: TMajorActionDiscardData) {
    super(data)
    this.cards = data.cards.map(cardFactory)
  }

  public userFacingDescription({
    tense,
    skin: _skin,
    gamePosition,
  }: TMajorActionUserFacingDescriptionData): string {
    const currentPlayer =
      gamePosition instanceof PlayerGamePosition
        ? gamePosition.currentPlayer
        : undefined
    const majorActor = gamePosition.majorActorPlayer
    const dropFirstNoun = tense === 'imperativePrompt'
    const isYou = currentPlayer?.id === majorActor.id
    const subject = dropFirstNoun ? undefined : isYou ? 'You' : majorActor.username

    // Any discard ends the majorActor's turn. Submitting one from a non-discard `play` is a legal
    // early end, labeled "early" here (only in the past-tense game log).
    const endedEarly = tense === 'past' && gamePosition.play !== 'discardPlay'
    const endTurnPhrase = (): string => {
      const verb = tense === 'past' ? 'ended' : 'end'
      return compact([
        subject,
        dropFirstNoun ? sentenceCase(verb) : verb,
        isYou ? 'your' : 'their',
        'turn',
        endedEarly ? 'early' : undefined,
      ]).join(' ')
    }

    if (this.cards.length === 0) {
      return endTurnPhrase()
    }

    // NB: the game log uses this, and we fake the cards in the card array (in `PastGamePosition`) since the backend
    // sends an empty array. So, don't try to print data about the discarded cards, just the count.
    const cardCount = `${this.cards.length} ${pluralize(this.cards.length, 'card')}`
    if (endedEarly) {
      return `${endTurnPhrase()}, discarding ${cardCount}`
    }
    return compact([
      subject,
      mutateIf(inTense(tense)('discard'), dropFirstNoun, sentenceCase),
      cardCount,
    ]).join(' ')
  }
}
